/**
 * Hilfe-Center: strukturierte Inhalte (rollenbewusst, grounded).
 *
 * GRUNDSATZ: Hier steht NUR, was die Anwendung heute wirklich kann.
 * Gates (OpenProject-Schreiben, E-Mail-Broadcast) sind ehrlich als
 * "mit Freigabe" bzw. "wartet auf ..." gekennzeichnet.
 * Stand: dev-v138 (Frontend) / dev-v142 (Orchestrator), 2026-07-21.
 */

export type HelpAudience = "berater" | "fachbereich" | "management";

export const AUDIENCE_LABEL: Record<HelpAudience, string> = {
  berater: "Berater:in (Impulse)",
  fachbereich: "1&1 Fachbereich",
  management: "Management / Steuerkreis",
};

export type HelpImage = { src: string; alt: string; beschriftung: string };

export type ModulHelp = {
  /** Anker-Id: im DOM als #modul-<id>; Kontexthilfe verlinkt hierauf. */
  id: string;
  route: string;
  titel: string;
  kurz: string;
  sehen: string[];
  tun: string[];
  hintergrund: string;
  daten: string;
  rollen: string;
  grenzen?: string;
  bilder?: HelpImage[];
  audience: HelpAudience[];
  tags: string[];
};

export type RolleGuide = {
  id: HelpAudience;
  titel: string;
  untertitel: string;
  wasIstDas: string;
  aufgaben: { text: string; route: string; linkLabel: string }[];
  ersterKlick: { text: string; route: string; linkLabel: string };
};

export type SystemBlock = {
  id: string;
  name: string;
  absatz: string;
  merksatz: string;
  beraterOnly?: boolean;
};

export type AgentZeile = {
  agent: string;
  wann: string;
  liest: string;
  erzeugt: string;
  sichtbar: string;
  beraterOnly?: boolean;
};

export type MatrixZeile = {
  taetigkeit: string;
  fachbereich: string;
  management: string;
  berater: string;
};

export type PortalLink = {
  name: string;
  url: string;
  zweck: string;
  zielgruppe: "kunde" | "berater";
};

export type FaqEintrag = {
  frage: string;
  antwort: string;
  route?: string;
  linkLabel?: string;
};

export type FeatureStand = {
  name: string;
  stand: "live" | "teilweise" | "geplant";
  text: string;
};

/* ================================================================== */
/* 1) EINSTIEG NACH ROLLE                                              */
/* ================================================================== */

export const ROLLEN_GUIDES: RolleGuide[] = [
  {
    id: "fachbereich",
    titel: "Ich arbeite bei 1&1 im Fachbereich",
    untertitel: "z. B. HR, Netz, Kundenservice, Controlling",
    wasIstDas:
      "Diese Anwendung ist das gemeinsame Arbeits-Cockpit für das Transformationsprogramm mit der Impulse-Beratung. " +
      "Sie sehen hier den Stand Ihres Ressorts (Ampeln, Kennzahlen, Aufgaben), liefern Eingaben zu (z. B. Formulare oder Notizen) " +
      "und können jede Aussage bis zu ihrer Quelle zurückverfolgen. Wichtig: Sie können hier nichts kaputt machen — " +
      "die Anwendung schreibt nie ungefragt in andere Systeme wie OpenProject.",
    aufgaben: [
      { text: "Auf der Startseite die \"Nächsten Schritte\" lesen — das ist Ihre priorisierte Aufgabenliste.", route: "/app/start", linkLabel: "Startübersicht" },
      { text: "Ihnen zugewiesene Formulare ausfüllen (z. B. Erhebungen oder den AI-Ready-Fragebogen).", route: "/app/formulare", linkLabel: "Formulare" },
      { text: "Den Stand Ihres Ressorts auf dem Kanban-Board verfolgen (Arbeitspakete, Vorgänge, Entscheidungen).", route: "/app/kanban", linkLabel: "Kanban-Board" },
      { text: "Ergebnisse aus Besprechungen nachlesen und offene Punkte prüfen.", route: "/app/meetings", linkLabel: "Meeting-Cockpit" },
      { text: "Erinnerungen einstellen, damit Sie Fälligkeiten und Verzug nicht verpassen.", route: "/app/benachrichtigungen", linkLabel: "Benachrichtigungen" },
    ],
    ersterKlick: {
      text: "Öffnen Sie die Startübersicht und lesen Sie den Kasten \"Nächste Schritte\" — er sagt Ihnen konkret, was heute ansteht.",
      route: "/app/start",
      linkLabel: "Zur Startübersicht",
    },
  },
  {
    id: "management",
    titel: "Ich bin Management / Steuerkreis",
    untertitel: "Programmleitung, Steuerkreis-Mitglied, Führungskraft",
    wasIstDas:
      "Für Sie ist das hier die eine Seite der Wahrheit zum Programm: Ampeln je Ressort und Phase, Kennzahlen mit Ziel und Beleg, " +
      "offene Entscheidungen und ein KI-Briefing, das ausschließlich aus den echten Programmdaten schreibt — nicht aus allgemeinem Weltwissen. " +
      "Sie entscheiden, das System dokumentiert: Jede Freigabe braucht eine Begründung und bleibt nachvollziehbar.",
    aufgaben: [
      { text: "Das Steuerkreis-Cockpit lesen: Programmfortschritt, KPI-Ampeln, Gate-Matrix und Datenlage auf einen Blick.", route: "/app/steuerkreis", linkLabel: "Steuerkreis-Cockpit" },
      { text: "Offene Entscheidungen prüfen und ausdrücklich freigeben, ablehnen oder vertagen — mit Begründung.", route: "/app/steuerungssystem", linkLabel: "Steuerungssystem" },
      { text: "Das Management-Briefing lesen oder neu erzeugen (dauert etwa 90 Sekunden, mit Belegen).", route: "/app/briefing-agent", linkLabel: "Management-Briefing" },
      { text: "Für Termine das Kundencockpit nutzen — verdichtet und beamer-tauglich.", route: "/app/kundencockpit", linkLabel: "Kundencockpit" },
      { text: "Schnelle Faktenfragen über \"Frag die Daten\" stellen (antwortet nur aus Programmdaten).", route: "/app/steuerkreis", linkLabel: "Frag die Daten" },
    ],
    ersterKlick: {
      text: "Öffnen Sie das Steuerkreis-Cockpit. Eine rote Ampel ist ein Einstiegssignal — der Klick darauf führt Sie zu Beleg und Ursache.",
      route: "/app/steuerkreis",
      linkLabel: "Zum Steuerkreis-Cockpit",
    },
  },
  {
    id: "berater",
    titel: "Ich bin Berater:in (Impulse)",
    untertitel: "Impulse / mf-impulse Beratungsteam",
    wasIstDas:
      "Ihr Steuerungs-Arbeitsplatz für das 1&1-Programm: Sie führen Prozessschritte der Methodik, pflegen Steuerungsfälle und KPI-Definitionen, " +
      "wählen passende Interventionen aus dem Katalog und bereiten Entscheidungen für den Steuerkreis vor. " +
      "Alles ist mandantengetrennt, jeder Schritt erzeugt einen prüfbaren Vorgang (Run) mit Beleg, und externe Systeme bleiben schreibgeschützt, " +
      "bis eine Übernahme ausdrücklich freigegeben ist.",
    aufgaben: [
      { text: "Prozessschritte der Methodik führen: Vorgang starten, Eingaben sammeln, analysieren, mit Nachweis abschließen.", route: "/app/process-landscape", linkLabel: "Prozesslandschaft" },
      { text: "Steuerungsfälle pflegen: Abweichung, Entscheidung, Maßnahme, Wirkung und nächsten Messzyklus verbinden.", route: "/app/steuerungssystem", linkLabel: "Steuerungssystem" },
      { text: "Zur Situation passende Interventionen finden — mit Rechtsvorbehalten und \"Nicht einsetzen\"-Hinweisen.", route: "/app/interventionen", linkLabel: "Interventionskatalog" },
      { text: "Steuerkreis vorbereiten: Briefing erzeugen, Kundencockpit prüfen, Entscheidungsvorlagen schärfen.", route: "/app/briefing-agent", linkLabel: "Management-Briefing" },
      { text: "Datenvertrauen prüfen: Sind Quellen erreichbar, aktuell und ausreichend gefüllt? Rote Gates zuerst beheben.", route: "/app/sources", linkLabel: "Datenquellen & Quality" },
    ],
    ersterKlick: {
      text: "Starten Sie im Steuerungssystem: Der Reiter \"Entscheidungen\" zeigt, wo heute eine menschliche Bewertung gebraucht wird.",
      route: "/app/steuerungssystem",
      linkLabel: "Zum Steuerungssystem",
    },
  },
];

/* ================================================================== */
/* 2) MODUL FÜR MODUL                                                  */
/* ================================================================== */

const ALLE: HelpAudience[] = ["berater", "fachbereich", "management"];
const BERATER: HelpAudience[] = ["berater"];

export const MODULE_HELP: ModulHelp[] = [
  {
    id: "start",
    route: "/app/start",
    titel: "Startübersicht (Single Pane)",
    kurz: "Der zentrale Einstieg: alle Module, Dashboards und Fachsysteme auf einer Seite — mit echten Live-Zählern.",
    sehen: [
      "Drei Gruppen von Kacheln: Steuerung & Analyse (App-Module), OpenSearch-Dashboards und angebundene Fachsysteme (Portal, OpenProject, Chatbot, Nextcloud).",
      "Live-Zahlen auf den Kacheln (z. B. Anzahl KPIs, offene Arbeitspakete) — echte Zählerstände, keine Werbezahlen.",
      "Den Kasten \"Nächste Schritte\": priorisierte Vorschläge der proaktiven KI mit Begründung und Direktlink.",
    ],
    tun: [
      "Oben prüfen, welcher Mandant und welche Sicht (Berater/Kunde) aktiv sind.",
      "\"Nächste Schritte\" lesen und den obersten Punkt direkt über seinen Link öffnen.",
      "Die passende Kachel anklicken — Dashboards öffnen bewusst in einem neuen Tab.",
    ],
    hintergrund:
      "Die Kachel-Zähler kommen aus einem Zähl-Dienst der Anwendung. Die \"Nächsten Schritte\" berechnet die proaktive KI aus Kennzahlen, " +
      "Ressort-Verzug, offenen Arbeitspaketen und Prozessständen; ein Prüf-Lauf aktualisiert die zugrunde liegenden Hinweise alle 15 Minuten.",
    daten:
      "Zähler: live bei jedem Seitenaufruf. Kennzahlen dahinter: KPI-Übernahme halbstündlich (:05/:35), geprüfte Gold-Daten halbstündlich (:12/:42), " +
      "OpenProject-Arbeitspakete täglich 05:30 Uhr.",
    rollen:
      "Alle Rollen sehen die Startseite. In der Kundensicht ist die Kachel-Auswahl verdichtet; die Beratersicht zeigt zusätzlich interne Steuerungsmodule.",
    grenzen:
      "Dashboards werden nicht eingebettet, sondern in neuem Tab geöffnet — das schützt Ihre Anmeldesitzung (SSO).",
    bilder: [
      {
        src: "/help/start-uebersicht.png",
        alt: "Startübersicht mit Programmstatus, Modul-Kacheln und Fachsystemen (synthetische Sandbox-Daten)",
        beschriftung: "Startübersicht: oben Mandant und Sicht prüfen, dann über die Kacheln in den passenden Arbeitsbereich.",
      },
    ],
    audience: ALLE,
    tags: ["start", "übersicht", "kacheln", "nächste schritte", "einstieg", "dashboard"],
  },
  {
    id: "steuerungssystem",
    route: "/app/steuerungssystem",
    titel: "Steuerungssystem (Regelkreis)",
    kurz: "Verbindet Abweichung, Entscheidung, Maßnahme und gemessene Wirkung zu einem nachvollziehbaren Steuerungsfall.",
    sehen: [
      "Fünf Ansichten: Steuerungsfälle, Entscheidungen, KPI-Definitionen, Fachpakete und Einrichtung.",
      "Je Steuerungsfall die komplette Kette: Kontext, Kennzahl, Auffälligkeit, Ursache, Entscheidung, Maßnahme, Wirkung, Lernen.",
      "Eine Entscheidungsliste mit Priorität (kritisch/hoch/…) — nur Punkte, an denen ein Mensch bewerten oder freigeben muss.",
      "In der Seitenleiste einen Live-Zähler \"N offen\", solange Entscheidungen warten.",
    ],
    tun: [
      "Unter \"Steuerungsfälle\" einen Fall öffnen oder mit \"Fall anlegen\" neu beginnen (KPI, Ausgangswert, Ziel, Zeitraum, Verantwortliche).",
      "\"Automatisierung ausführen\": automatische Prüfschritte liefern Vorschläge und Belege — fachliche und rechtliche Prüfung bleibt beim Menschen.",
      "Unter \"Entscheidungen\" mit kritisch/hoch beginnen: Fall öffnen, Aussage/Quelle/Konfidenz prüfen, dann freigeben, ablehnen oder vertagen — Begründung und Nachweis sind Pflicht.",
      "Nach der Umsetzung die Wirkung erfassen und den nächsten Messzeitpunkt setzen — erst damit schließt sich der Regelkreis.",
      "Unter \"KPI-Definitionen\" Kennzahlen vollständig beschreiben (Formel, Einheit, Ziel, Schwellen, Verantwortliche).",
      "Unter \"Fachpakete\" freigegebene Branchen-/Prozess-/KPI-Bausteine übernehmen; unter \"Einrichtung\" den Kontext \"aus Bestand ableiten\".",
    ],
    hintergrund:
      "Alle Schritte laufen über die Steuerungs-Schnittstelle der Anwendung; jede Freigabe wird mit Begründung und Nachweis im Fall festgehalten. " +
      "Automatische Prüfschritte erzeugen Vorschläge mit Beleg — sie entscheiden nie selbst.",
    daten:
      "Steuerungsfälle, Entscheidungen und Definitionen liegen mandantengetrennt in der Anwendungsdatenbank. " +
      "Kennzahlen-Istwerte kommen aus dem geprüften Gold-Leseweg (halbstündlich aktualisiert).",
    rollen:
      "Fachbereich: mitlesen und zuliefern. Management: Entscheidungen freigeben/ablehnen/vertagen. " +
      "Berater:in: Fälle, KPI-Definitionen, Fachpakete und Einrichtung pflegen.",
    grenzen:
      "Eine Freigabe hier löst keine verdeckte Änderung in OpenProject oder im Lakehouse aus — externe Systeme bleiben schreibgeschützt.",
    bilder: [
      {
        src: "/help/steuerungssystem.png",
        alt: "Steuerungsfall mit vollständiger Wirkungskette (synthetischer Referenzfall)",
        beschriftung: "Steuerungsfall: die Kette von der Auffälligkeit bis zur gemessenen Wirkung — mit offenen menschlichen Übergaben.",
      },
      {
        src: "/help/entscheidungen.png",
        alt: "Priorisierte offene Entscheidungen eines synthetischen Steuerungsfalls",
        beschriftung: "Entscheidungen: Priorität, Beleg und Fallkontext stehen zusammen, bevor ein Mensch entscheidet.",
      },
      {
        src: "/help/kennzahlen.png",
        alt: "KPI-Definitionen mit Vollständigkeit, Ziel, Verantwortung und Nachweisen",
        beschriftung: "KPI-Definitionen: Definition, Ziel, Schwellen und Nachweis werden getrennt und sichtbar geführt.",
      },
      {
        src: "/help/fachpakete.png",
        alt: "Freigegebene Branchen-, Prozess-, KPI- und Anwendungsfallpakete",
        beschriftung: "Fachpakete: bewährtes Wissen übernehmen, ohne die Herkunft zu verlieren.",
      },
      {
        src: "/help/einrichtung.png",
        alt: "Einrichtungsansicht für Unternehmen, Branche, Prozesse, Quellen und Datenqualität",
        beschriftung: "Einrichtung: vorhandenen Kontext strukturiert ableiten statt mit stillen Annahmen zu starten.",
      },
    ],
    audience: ALLE,
    tags: ["steuerungsfall", "regelkreis", "entscheidung", "freigabe", "kpi-definition", "fachpaket", "einrichtung", "wirkung"],
  },
  {
    id: "steuerkreis",
    route: "/app/steuerkreis",
    titel: "Steuerkreis-Cockpit",
    kurz: "Die eine Seite für den Steuerkreis-Termin: Ampeln, Gate-Matrix, Briefing und \"Frag die Daten\".",
    sehen: [
      "Programmfortschritt und KPI-Ampeln mit Ist, Ziel und Zielerreichung — Kennzahlen ohne Messwert sind ehrlich als Platzhalter markiert.",
      "Die Phasen-Gate-Matrix: je Ressort und Phase eine Ampel (grün im Plan, gelb nicht begonnen, rot in Verzug) aus dem OE-Board.",
      "Das letzte KI-Briefing und die aktuelle Datenlage (Vertrauens-Gate).",
      "Je Kennzahl eine Leiste mit Direktsprüngen: Dashboard, Treiberbaum, passende Intervention, Belege & Runs, OpenProject.",
    ],
    tun: [
      "Vor dem Termin die Ampeln lesen; bei Rot dem Link zu Beleg und Ursache folgen — eine Ampel ist ein Einstiegssignal, keine fertige Entscheidung.",
      "Im Feld \"Frag die Daten\" eine konkrete Faktenfrage stellen (z. B. \"Welche Ressorts sind in Verzug?\").",
      "Bei Bedarf ein frisches Briefing erzeugen oder den Export für die Sitzung nutzen.",
    ],
    hintergrund:
      "\"Frag die Daten\" beantwortet Fragen ausschließlich aus den echten Programmdaten mit dem lokalen KI-Modell — ohne Weltwissen; " +
      "fehlt eine Information, sagt die Antwort das ausdrücklich. Kein Datensatz verlässt die Plattform.",
    daten:
      "Kennzahlen aus dem geprüften Gold-Leseweg (halbstündlich), Gate-Matrix aus dem OE-Board, Arbeitspakete aus OpenProject (täglich 05:30 Uhr).",
    rollen: "Für alle sichtbar — gedacht für Management/Steuerkreis; Berater:innen nutzen es zur Terminvorbereitung.",
    bilder: [
      {
        src: "/help/steuerkreis.png",
        alt: "Steuerkreis-Cockpit mit Ampeln, Gate-Matrix und Frag-die-Daten (synthetische Sandbox)",
        beschriftung: "Steuerkreis-Cockpit: Ampeln lesen, bei Rot dem Beleg folgen, Faktenfragen unten stellen.",
      },
    ],
    audience: ALLE,
    tags: ["steuerkreis", "cockpit", "ampel", "gate-matrix", "frag die daten", "briefing", "termin"],
  },
  {
    id: "kundencockpit",
    route: "/app/kundencockpit",
    titel: "Kundencockpit",
    kurz: "Die verdichtete, beamer-taugliche Sicht für Steuerkreis- und Kundentermine.",
    sehen: [
      "Programm-Ampel gesamt, KPI-Ampeln, Gate-Matrix (Ressorts × Phasen), Teilprojekte, das letzte Briefing, die wichtigsten offenen Entscheidungen und nächste Schritte.",
    ],
    tun: [
      "Vor dem Termin öffnen und einmal durchscrollen — alles Weitere sind Direktlinks in die Detailmodule.",
      "Im Termin als gemeinsame Gesprächsgrundlage verwenden (große Schrift, klare Ampeln).",
    ],
    hintergrund:
      "Ein reiner Lese-Aggregator: Er setzt bestehende, belegte Bausteine zusammen (OE-Board, Kennzahlen, Entscheidungs-Log, Briefing) und erfindet nichts Neues.",
    daten: "Dieselben Quellen wie das Steuerkreis-Cockpit — OE-Board, Gold-Kennzahlen, Entscheidungs-Log, Briefing-Archiv.",
    rollen:
      "Für alle sichtbar. In der Kundensicht werden interne Steuerungsdetails (z. B. interne Verzugs-Kommentare) bewusst weggelassen.",
    bilder: [
      {
        src: "/help/kundencockpit.png",
        alt: "Kundencockpit mit Programm-Ampel, KPI-Ampeln und offenen Entscheidungen (synthetische Sandbox)",
        beschriftung: "Kundencockpit: die verdichtete Termin-Sicht — Ampeln, Entscheidungen, nächste Schritte.",
      },
    ],
    audience: ALLE,
    tags: ["kundencockpit", "termin", "beamer", "verdichtet", "management"],
  },
  {
    id: "process-landscape",
    route: "/app/process-landscape",
    titel: "Prozesslandschaft (geführte Methodik)",
    kurz: "Der Fahrplan der Beratungs-Methodik: 12 Phasen, 59 Teilprozesse, 26 KI-Agenten — mit geführtem Arbeitsraum je Schritt.",
    sehen: [
      "Alle Phasen und Teilprozesse (z. B. \"1.2.4 Vertragsgestaltung\") mit Eingaben, Ergebnissen und zuständigem KI-Agenten.",
      "Je Schritt fünf geführte Bereiche: Was passiert · Wer liefert was · Wie entsteht das Ergebnis · Wer schließt ab (RACI) · Was wird wo abgelegt.",
      "Leitfragen zur Analyse und automatisch passende Dokumente aus der Wissensbasis.",
    ],
    tun: [
      "Schritt öffnen und den Bereich \"Wer liefert was\" nutzen: Notizen, Links und Unterlagen beitragen (das dürfen ausdrücklich auch Fachbereiche).",
      "Berater:in: \"Vorgang starten\" — der Schritt erscheint sofort unter Agent Runs & Evidence.",
      "Analyse nutzen: Leitfragen beantworten, Wissensbasis-Treffer prüfen, lokale KI-Analyse erzeugen.",
      "\"Mit Nachweis abschließen\": kurze Ergebnis-Notiz eintragen — ohne Nachweis zählt der Schritt bewusst nicht als erledigt.",
    ],
    hintergrund:
      "Der Abschluss schreibt einen Vorgang in den Runs-Index und aktualisiert das OE-Board (Quelle: Prozess-Run). " +
      "Die Wissensbasis-Suche kombiniert Volltext- und Bedeutungssuche über die ingestierten Dokumente; die Analyse nutzt das lokale KI-Modell.",
    daten:
      "Methodik aus dem Methodik-Index (Leitfragen je Schritt), Wissensbasis aus den ingestierten Plattform-Dokumenten (Nextcloud/Protokolle), " +
      "Fortschritt zählt nur tatsächlich abgeschlossene Schritte.",
    rollen:
      "Starten und Abschließen können nur Berater:in/Admin — andere Rollen erhalten eine verständliche Meldung. " +
      "Zuliefern (Notizen, Links, Dateien) können alle. In der Kundensicht sind die Aktions-Knöpfe ausgeblendet.",
    grenzen:
      "Der optionale Übertrag nach OpenProject läuft nur als Probelauf (Dry-Run): Die Anwendung zeigt den geplanten Kommentar vollständig an, schreibt ihn aber nicht — echtes Schreiben bleibt hinter einer ausdrücklichen Freigabe der Inhaberin gesperrt.",
    bilder: [
      {
        src: "/help/prozesslandschaft.png",
        alt: "Prozesslandschaft mit Phasen und Teilprozessen (synthetische Sandbox)",
        beschriftung: "Prozesslandschaft: der Methodik-Fahrplan — jeder Schritt öffnet einen geführten Arbeitsraum.",
      },
    ],
    audience: ALLE,
    tags: ["prozess", "methodik", "phase", "teilprozess", "vorgang", "nachweis", "raci", "analyse"],
  },
  {
    id: "interventionen",
    route: "/app/interventionen",
    titel: "Interventionskatalog",
    kurz: "Von der Situation zur richtigen Maßnahme: kuratierte Interventionen mit Wirkung, Grenzen und Rechtsvorbehalten.",
    sehen: [
      "Den kuratierten Katalog (72 Impulse-Interventionen; für 1&1 zusätzlich 85 mandantenspezifische) mit Filtern nach Domäne, Phase, Härtegrad und Volltextsuche.",
      "Je Intervention alle Felder im Wortlaut — inklusive rot hervorgehobener \"Nicht einsetzen\"-Hinweise und rechtlicher Vorbehalte (z. B. Mitbestimmung nach BetrVG).",
    ],
    tun: [
      "Situation in eigenen Worten in die Suche eingeben (\"Team blockiert Veränderung\") — die Trefferliste schlägt passende Interventionen vor.",
      "Vor dem Einsatz immer die Felder \"Nicht einsetzen\" und den rechtlichen Vorbehalt lesen.",
      "Den Interventions-Code (z. B. IV-KVP) in Entscheidungen, Formularen oder Maßnahmen referenzieren.",
    ],
    hintergrund:
      "Die KI sucht und sortiert nur (Volltext + Bedeutungssuche + Reranking) — der Inhalt kommt immer im Wortlaut aus der geprüften Gold-Ebene. " +
      "Ohne passenden Treffer sagt das System ehrlich \"nichts gefunden\", statt zu erfinden.",
    daten:
      "Gold-Tabellen des Lakehouse; Anreicherung über einen kontrollierten Excel-Eingang, der alle 10 Minuten geprüft übernommen wird (mit Versionierung und Quarantäne).",
    rollen: "Beratersicht: voller Katalog und Auswahlarbeit. Mandanten sind strikt getrennt — 1&1 sieht nie Impulse-interne Einträge anderer Kunden und umgekehrt.",
    bilder: [
      {
        src: "/help/interventionen.png",
        alt: "Interventionskatalog mit Filtern und Detailfeldern (synthetische Sandbox)",
        beschriftung: "Interventionskatalog: filtern, Wortlaut lesen — inklusive Nicht-einsetzen-Hinweisen und Rechtsvorbehalt.",
      },
    ],
    audience: BERATER,
    tags: ["intervention", "katalog", "maßnahme", "betrvg", "rechtlicher vorbehalt", "situation"],
  },
  {
    id: "meetings",
    route: "/app/meetings",
    titel: "Meeting-Cockpit",
    kurz: "Freigegebene Besprechungs-Protokolle nachlesen und in Aufgaben oder Entscheidungen überführen.",
    sehen: [
      "Nur tatsächlich indexierte Protokolle (aktuell 13) mit Art, Datum und Quelle — keine Roh-Aufnahmen.",
      "Je Protokoll auf Wunsch: KI-Zusammenfassung, erkannte Aufgaben (Action-Items) und Entscheidungen — jeweils mit Quellenbeleg.",
    ],
    tun: [
      "Protokoll auswählen, zuerst Quelle und Zeitpunkt prüfen.",
      "Zusammenfassung erzeugen und gegen den Text prüfen — sie ist ein Vorschlag, keine Wahrheit.",
      "Bestätigte Punkte mit \"Als Entscheidung übernehmen\" ins Entscheidungs-Log überführen.",
    ],
    hintergrund:
      "Die Zusammenfassung entsteht lokal und ausschließlich aus dem vorliegenden Text: Ein Foto-Protokoll ohne Aufgaben ergibt ehrlich null Aufgaben. " +
      "Ins System gelangt nur die freigegebene Ableitung eines Meetings — die Rohaufnahme bleibt geschützt und wird nie frei durchsuchbar.",
    daten:
      "Wissensindex der Plattform (Meetily-Ableitungen über den freigegebenen Pfad sowie Nextcloud-Protokolle); neue Dokumente erscheinen automatisch nach dem nächsten Ingest-Lauf.",
    rollen: "Für alle sichtbar (mandantengetrennt). Übernahmen ins Entscheidungs-Log kann jede:r Berechtigte auslösen.",
    grenzen: "Ein etwaiger Übertrag nach OpenProject bleibt Probelauf (Dry-Run) — es wird nichts automatisch in Fremdsysteme geschrieben.",
    bilder: [
      {
        src: "/help/meetings.png",
        alt: "Meeting-Cockpit mit indexierten Protokollen und Analyse-Panel (synthetische Sandbox)",
        beschriftung: "Meeting-Cockpit: nur freigegebene, indexierte Protokolle — Zusammenfassungen bleiben Vorschläge.",
      },
    ],
    audience: ALLE,
    tags: ["meeting", "protokoll", "transkript", "zusammenfassung", "action items", "meetily"],
  },
  {
    id: "decisions",
    route: "/app/decisions",
    titel: "Entscheidungs-Log",
    kurz: "Entscheidungen erfassen, begründen, verknüpfen und nachverfolgen — damit später klar ist, was warum entschieden wurde.",
    sehen: [
      "Eine Liste mit Status-Reitern (Alle/Offen/Entschieden) und Suche.",
      "Je Eintrag: Titel, Kontext, Optionen, Entscheidung, Begründung, Entscheider:in, Rolle, Datum und Verknüpfungen (z. B. zu Kennzahl oder Arbeitspaket).",
    ],
    tun: [
      "Neue Entscheidung anlegen — oder sie entsteht vorbefüllt aus einem Prozessschritt oder Meeting.",
      "Auf Wunsch den lokal erzeugten KI-Vorschlag nutzen (deutlich als \"KI-Vorschlag\" markiert; er überschreibt nie Ihre Eingaben).",
      "Status pflegen und die Entscheidung mit Belegen verknüpfen.",
    ],
    hintergrund: "Reines Protokoll-Modul mit lokalem Vorschlags-Helfer — es entscheidet nichts selbst.",
    daten: "Eigene Datenbanktabelle, strikt je Mandant und Benutzer:in getrennt.",
    rollen: "Alle können lesen; erfassen und ändern gemäß Berechtigung. In der Kundensicht sichtbar.",
    bilder: [
      {
        src: "/help/entscheidungslog.png",
        alt: "Entscheidungs-Log mit Status-Reitern und Editor (synthetische Sandbox)",
        beschriftung: "Entscheidungs-Log: Was wurde warum entschieden — mit Verknüpfungen und Status.",
      },
    ],
    audience: ALLE,
    tags: ["entscheidung", "log", "begründung", "nachvollziehbar", "protokoll"],
  },
  {
    id: "kanban",
    route: "/app/kanban",
    titel: "Kanban-Board",
    kurz: "Ein Board über alles, was läuft: Arbeitspakete, Vorgänge, Entscheidungen und eigene Karten — mit klaren Schreibregeln.",
    sehen: [
      "Rund 206 Karten aus echten Quellen: 108 OpenProject-Arbeitspakete (nur Anzeige), Vorgänge (Runs), Entscheidungen und eigene Karten.",
      "Vier Spalten (Offen, In Arbeit, Review, Erledigt), umschaltbare Schwimmbahnen je Ressort, konfigurierbare Spalten je Ansicht und WIP-Warnungen bei Überlast.",
      "Filter nach Ressort, Teilprojekt, Programm, Typ und Freitext.",
    ],
    tun: [
      "Eigene Karten anlegen, per Drag & Drop verschieben und innerhalb der Spalte sortieren — das wird dauerhaft gespeichert.",
      "OpenProject-Karten öffnen (Direktlink ins Projektsystem), um Details zu lesen.",
      "Statuswechsel an einer OpenProject-Karte anstoßen: Das erzeugt einen Freigabe-Eintrag (Arbeitspaket, alt → neu, Begründung) zur menschlichen Prüfung.",
    ],
    hintergrund:
      "Das Board fasst nur zusammen; OpenProject wird täglich um 05:30 Uhr gelesen. Freigegebene Statuswechsel erhalten den Status " +
      "\"freigegeben — wartet auf OpenProject-Übernahme\" und werden dokumentiert.",
    daten: "Arbeitspakete aus dem OpenProject-Index (täglich), Vorgänge aus dem Run-Speicher, eigene Karten aus der Anwendungsdatenbank (je Mandant).",
    rollen: "Alle sehen ihr Board (Kundensicht inklusive). Eigene Karten: voll bearbeitbar. Arbeitspaket-Karten: nur Anzeige plus Freigabe-Weg.",
    grenzen:
      "Die Anwendung schreibt nie selbst nach OpenProject: Auch eine erteilte Freigabe wartet auf die ausdrücklich freigeschaltete Übernahme (derzeit deaktiviert).",
    bilder: [
      {
        src: "/help/kanban.png",
        alt: "Kanban-Board mit Spalten, Filtern und Karten aus echten Quellen (synthetische Sandbox)",
        beschriftung: "Kanban-Board: eigene Karten frei beweglich, OpenProject-Karten nur Anzeige plus Freigabe-Weg.",
      },
    ],
    audience: ALLE,
    tags: ["kanban", "board", "arbeitspaket", "wip", "swimlane", "drag", "freigabe", "openproject"],
  },
  {
    id: "formulare",
    route: "/app/formulare",
    titel: "Formulare",
    kurz: "Erhebungen, Checklisten und Auftragsklärungen bauen, zuweisen und auswerten — ohne E-Mail-Ping-Pong.",
    sehen: [
      "Eine Liste aller Formulare mit Status, Feld- und Antwortzahl.",
      "Vier fachlich hinterlegte Vorlagen aus dem Methodik-/Interventionskatalog (u. a. der AI-Ready-Fragebogen mit 21 Fragen) plus \"Leeres Formular\".",
      "Je Formular eine Antworten-Tabelle mit einfacher Auswertung (Zählungen, Mittelwerte) und CSV-Export.",
    ],
    tun: [
      "Berater:in: Formular aus Vorlage oder frei bauen (Text, Zahl, Auswahl, Datum, Checkbox; Pflichtfelder und Hilfetexte).",
      "Formular einer Rolle zuweisen — die Empfänger sehen einen Hinweis in ihren \"Nächsten Schritten\".",
      "Fachbereich: zugewiesene Formulare ausfüllen und absenden.",
      "Antworten ansehen, auswerten und bei Bedarf als CSV (Excel-tauglich) exportieren.",
    ],
    hintergrund: "Zuweisungen erzeugen einen proaktiven Hinweis; Antworten werden mandantengetrennt gespeichert und einfach aggregiert.",
    daten: "Eigene Datenbanktabellen (Formulare und Antworten), je Mandant und Benutzer:in getrennt.",
    rollen: "Berater:in erstellt, weist zu und wertet aus; Fachbereich füllt aus. In der Kundensicht sichtbar.",
    bilder: [
      {
        src: "/help/formulare.png",
        alt: "Formular-Liste mit Vorlagen und Antwort-Zählern (synthetische Sandbox)",
        beschriftung: "Formulare: aus Vorlagen bauen, zuweisen, ausfüllen lassen, auswerten und als CSV exportieren.",
      },
    ],
    audience: ALLE,
    tags: ["formular", "erhebung", "checkliste", "csv", "vorlage", "zuweisung", "ai-ready"],
  },
  {
    id: "benachrichtigungen",
    route: "/app/benachrichtigungen",
    titel: "Erinnerungen & Benachrichtigungen",
    kurz: "Die Glocke oben rechts plus Einstellseite: Auslöser, Kanäle und Häufigkeit selbst bestimmen.",
    sehen: [
      "Alle Hinweise in der Glocke (auf jeder Seite) und als Liste auf dieser Seite.",
      "Einstellbare Auslöser: KPI-Schwellwert, Ressort-Verzug, überfällige Arbeitspakete, offene Prozessschritte, fehlende Nachweise, Fälligkeiten, Inaktivität.",
      "Häufigkeit je Auslöser: sofort, täglich oder wöchentlich.",
    ],
    tun: [
      "Auslöser aktivieren und die Häufigkeit wählen.",
      "Hinweise in der Glocke als gelesen markieren oder alle auf einmal.",
      "Auf einen Hinweis klicken — er führt direkt zum betroffenen Objekt.",
    ],
    hintergrund:
      "Ein Prüf-Lauf kontrolliert alle 15 Minuten die eingestellten Auslöser und ist gegen Doppelmeldungen geschützt (dieselbe Sache wird nicht mehrfach gemeldet, offene Punkte kommen zur Wiedervorlage).",
    daten: "Eigene Datenbanktabellen; die Auslöser lesen dieselben geprüften Programmdaten wie die Cockpits.",
    rollen: "Jede:r stellt die eigenen Benachrichtigungen ein (je Mandant und Person).",
    grenzen:
      "In-App ist live. E-Mail-Versand ist fertig eingerichtet (mit Schutz vor Doppelversand), wartet aber noch auf das bestätigte Postfach-Passwort — bis dahin kommen alle Hinweise zuverlässig über die Glocke. Ein Chat-Kanal ist geplant.",
    bilder: [
      {
        src: "/help/benachrichtigungen.png",
        alt: "Einstellseite für Erinnerungen und Benachrichtigungen (synthetische Sandbox)",
        beschriftung: "Benachrichtigungen: Auslöser und Häufigkeit selbst bestimmen — die Glocke sammelt alles ein.",
      },
    ],
    audience: ALLE,
    tags: ["benachrichtigung", "erinnerung", "glocke", "e-mail", "auslöser", "frequenz"],
  },
  {
    id: "briefing-agent",
    route: "/app/briefing-agent",
    titel: "Management-Briefing (KI)",
    kurz: "Ein echter Hintergrund-Agent schreibt den Steuerkreis-Bericht — nur aus den Programmdaten, mit Belegen.",
    sehen: [
      "Das aktuelle Briefing in Prosa: Was hat sich verändert, wo ist Verzug, welche Maßnahme wird empfohlen.",
      "Eine ehrliche Agenten-Statustabelle (was läuft wirklich, was ist teilweise, was inaktiv).",
      "Den Export-Bereich (\"Board-Pack\").",
    ],
    tun: [
      "\"Briefing erzeugen\" starten — der Lauf dauert etwa 90 Sekunden und erscheint parallel unter Agent Runs.",
      "Das Ergebnis lesen und Aussagen bei Bedarf über die Belege prüfen.",
      "Bei Bedarf als Datei exportieren (Markdown, CSV, Excel, JSON); Download-Links gelten 7 Tage.",
    ],
    hintergrund:
      "Ein Hintergrund-Worker sammelt den Faktenblock (Kennzahlen, Treiberbaum, Datenvertrauen, Arbeitspakete/OE-Board) und lässt das lokale KI-Modell " +
      "ausschließlich daraus formulieren. Kein Inhalt verlässt die Plattform.",
    daten: "Gold-Kennzahlen, OE-Board und OpenProject-Stand — dieselben geprüften Quellen wie in den Cockpits.",
    rollen: "Für alle lesbar (auch Kundensicht); erzeugen kann jede:r Berechtigte.",
    grenzen: "PDF/PowerPoint werden bewusst nicht erzeugt — die verlässlichen Formate sind Markdown/CSV/Excel/JSON.",
    bilder: [
      {
        src: "/help/briefing-agent.png",
        alt: "Management-Briefing mit Bericht, Agenten-Status und Export (synthetische Sandbox)",
        beschriftung: "Management-Briefing: der Bericht entsteht nur aus dem Faktenblock — Status und Export daneben.",
      },
    ],
    audience: ALLE,
    tags: ["briefing", "bericht", "ki", "export", "board-pack", "steuerkreis"],
  },
  {
    id: "advisor",
    route: "/app/advisor",
    titel: "KI-Berater (Advisor)",
    kurz: "Sparring zu Kennzahlen, Use Cases und Strategie — jede Antwort mit Beleg-Chips aus der Wissensbasis.",
    sehen: [
      "Einen Chat, wählbar je Führungsrolle (CEO, CFO, COO, CHRO …).",
      "Unter jeder Antwort ein Grounding-Hinweis (\"aus der Wissensbasis, N Belege\") plus klickbare Beleg-Chips zu Quelle und Zitat.",
    ],
    tun: [
      "Eine konkrete Frage stellen (\"Welche Kennzahl gefährdet das Ziel im Ressort X?\").",
      "Die Beleg-Chips öffnen und die Quelle prüfen, bevor Sie Schlüsse ziehen.",
    ],
    hintergrund:
      "Der Advisor nutzt dieselbe Wissensbasis-Suche wie der geführte Prozessschritt (Volltext + Bedeutungssuche) und formuliert lokal. " +
      "Ist das Modell nicht erreichbar, sagt die Antwort das ehrlich (\"eingeschränkt\") und zeigt trotzdem die Belege.",
    daten: "Ingestierte Plattform-Inhalte (Dokumente, Protokolle, Programm-Daten) des eigenen Mandanten.",
    rollen: "Beratersicht. Für schnelle Faktenfragen im Termin nutzen Management/Fachbereich \"Frag die Daten\" im Steuerkreis-Cockpit.",
    grenzen:
      "Abgrenzung: \"Frag die Daten\" (Steuerkreis) = kurze Faktenfrage zum Programm · Advisor = Einordnung und Sparring · " +
      "1&1-KIM-Chat = Wissensrecherche und Interventionsempfehlung im Chatbot · Impulse-Chat = Berater-Methodik über Mandanten hinweg.",
    bilder: [
      {
        src: "/help/advisor.png",
        alt: "KI-Berater-Chat mit Grounding-Hinweis und Beleg-Chips (synthetische Sandbox)",
        beschriftung: "KI-Berater: jede Antwort trägt Belege — Quelle prüfen gehört zum Arbeitsablauf.",
      },
    ],
    audience: BERATER,
    tags: ["advisor", "ki-berater", "chat", "beleg", "grounding", "sparring"],
  },
  {
    id: "briefings",
    route: "/app/briefings",
    titel: "Briefings (Archiv)",
    kurz: "Ältere Briefing-Einträge zum Nachschlagen.",
    sehen: ["Die Liste früherer Briefings mit Zeitpunkt und Status."],
    tun: ["Einträge öffnen, um die Historie nachzuvollziehen; das aktuelle Briefing lebt im Modul Management-Briefing."],
    hintergrund: "Reines Archiv der Briefing-Läufe.",
    daten: "Briefing-Speicher der Anwendung (je Mandant).",
    rollen: "Beratersicht; Management liest das aktuelle Briefing im Briefing-Modul oder Steuerkreis-Cockpit.",
    audience: BERATER,
    tags: ["briefing", "archiv", "historie"],
  },
  {
    id: "metrics",
    route: "/app/metrics",
    titel: "KPI-Katalog",
    kurz: "Alle Kennzahlen-Definitionen (Metric Contracts) mit Ist, Ziel und Zielerreichung.",
    sehen: [
      "Alle definierten Kennzahlen (aktuell 13, davon 8 mit echtem Messwert) — fehlende Messwerte sind ehrlich als Platzhalter markiert.",
      "Je Kennzahl Einheit, Richtung (höher/niedriger ist besser) und Zielwert.",
    ],
    tun: [
      "Definitionen prüfen und vervollständigen (Beratersicht).",
      "Vom Platzhalter aus dem Datenweg folgen: Unter Datenquellen prüfen, warum noch kein Messwert fließt.",
    ],
    hintergrund: "Definition und Messwert sind bewusst getrennt: Ein Wert allein sagt nicht, ob er gut oder schlecht ist.",
    daten: "Definitionen aus der Anwendungsdatenbank; Ist-Werte über den geprüften Werte-Weg (halbstündlich).",
    rollen: "Lesen: alle (in Kundensicht enthalten). Anlegen/Ändern: Beratersicht.",
    bilder: [
      {
        src: "/help/kpi-katalog.png",
        alt: "KPI-Katalog mit Definitionen, Zielen und Platzhalter-Kennzeichnung (synthetische Sandbox)",
        beschriftung: "KPI-Katalog: Definition und Messwert getrennt — Platzhalter sind ehrlich markiert.",
      },
    ],
    audience: ALLE,
    tags: ["kpi", "kennzahl", "katalog", "metric contract", "ziel", "platzhalter"],
  },
  {
    id: "candidates",
    route: "/app/candidates",
    titel: "KPI-Kandidaten",
    kurz: "Vorschlagsliste neuer Kennzahlen — Review, bevor echte Werte angezeigt werden.",
    sehen: ["Kandidaten mit Herkunft und Status."],
    tun: ["Kandidaten prüfen und geeignete zu vollwertigen KPI-Definitionen befördern."],
    hintergrund: "Qualitätsschleuse: Nichts wird still zur \"offiziellen\" Kennzahl.",
    daten: "Anwendungsdatenbank (je Mandant).",
    rollen: "Beratersicht.",
    audience: BERATER,
    tags: ["kpi", "kandidat", "review"],
  },
  {
    id: "trees",
    route: "/app/trees",
    titel: "Driver Trees (Werttreiberbäume)",
    kurz: "Die Ursache-Wirkungs-Landkarte: welcher Hebel wirkt auf welche Kennzahl.",
    sehen: [
      "Den 1&1-Baum \"OE & Netz-KI\" mit 13 Knoten und 12 Kanten: oben Wertbeitrag, darunter Handlungsfelder, unten messbare Kennzahlen.",
      "Farbcodierung nach Zielerreichung (grün ab 90 %, gelb ab 70 %, sonst rot); Handlungsfelder verlinken auf OpenProject-Arbeitspakete.",
    ],
    tun: [
      "Knoten anklicken: Ist, Ziel, Zielerreichung und Belege ansehen.",
      "Berater:in: Knoten ergänzen und Kennzahlen zuordnen.",
    ],
    hintergrund: "Die Kennzahlen an den Knoten kommen aus demselben geprüften Werte-Weg wie im Cockpit.",
    daten: "Baumstruktur aus der Anwendungsdatenbank, Werte halbstündlich, Arbeitspakete täglich.",
    rollen: "Lesen: alle (Kundensicht enthalten). Bearbeiten: Beratersicht.",
    bilder: [
      {
        src: "/help/treiberbaum.png",
        alt: "Werttreiberbaum mit farbcodierten Knoten (synthetische Sandbox)",
        beschriftung: "Driver Tree: vom Wertbeitrag über Handlungsfelder zu messbaren Kennzahlen.",
      },
    ],
    audience: ALLE,
    tags: ["driver tree", "werttreiber", "ursache", "wirkung", "hebel"],
  },
  {
    id: "sources",
    route: "/app/sources",
    titel: "Datenquellen & Quality",
    kurz: "Das Vertrauens-Gate: Welche Quellen sind angeschlossen, wie aktuell und wie gut sind sie?",
    sehen: [
      "Die Vertrauens-Checkliste mit den release-kritischen Punkten — sie erklärt, warum Werte ggf. noch gesperrt sind.",
      "Je Quelle: Erreichbarkeit, letzter Sync, Datenvolumen, Quality-Score (ab 75 Punkten \"vertrauenswürdig\") und Belege.",
    ],
    tun: [
      "Bei rotem Gate zuerst die Ursache beheben (fehlender Report, veralteter Sync) — nie die Schwelle umgehen.",
      "Neue Datenlieferungen (CSV/Excel) über den kontrollierten Weg anstoßen.",
      "Erst nach grünem Gate Kennzahlen als belastbare Entscheidungsgrundlage verwenden.",
    ],
    hintergrund: "Echte Einzelprüfungen je Quelle — lieber ein leeres Cockpit mit Begründung als erfundene Zahlen.",
    daten: "Prüfergebnisse live je Aufruf; die geprüften Quellen selbst aktualisieren sich nach ihren Rhythmen (siehe Agenten-Landkarte).",
    rollen: "Beratersicht und Admin; Fachbereich/Management sehen die Wirkung im Cockpit (Gate-Hinweise).",
    audience: BERATER,
    bilder: [
      {
        src: "/help/datenquellen.png",
        alt: "Data-Trust-Checkliste mit klar markierten Blockern (synthetische Sandbox)",
        beschriftung: "Datenvertrauen: Die Checkliste sagt ehrlich, welche Voraussetzungen für belastbare Zahlen fehlen.",
      },
    ],
    tags: ["quelle", "quality", "trust", "gate", "sync", "score", "evidence"],
  },
  {
    id: "agents",
    route: "/app/agents",
    titel: "Agent Runs & Evidence",
    kurz: "Das Protokoll aller KI- und Prozess-Läufe: Wer hat wann was erzeugt — mit Belegkette.",
    sehen: [
      "Alle Vorgänge (Runs) mit Status (wartet/läuft/fertig), Zeitstempel und Belegen; jeder Lauf trägt eine Hash-Kette.",
      "Gestartete Prozessschritte und Briefing-Läufe erscheinen hier sofort.",
    ],
    tun: [
      "Bei Fragen (\"Woher kommt diese Aussage?\") den zugehörigen Run öffnen und die Belege lesen.",
      "Bei Störungen die Run-ID notieren — sie macht Diagnosen schnell.",
    ],
    hintergrund: "Zentrales Nachvollziehbarkeits-Register der Plattform.",
    daten: "Run-Speicher der Anwendung (je Mandant).",
    rollen: "Beratersicht und Admin; Auszüge fließen in Cockpits und Kanban ein.",
    audience: BERATER,
    tags: ["run", "evidence", "beleg", "protokoll", "audit", "hash"],
  },
  {
    id: "transformation",
    route: "/app/transformation",
    titel: "Transformation OS (1&1)",
    kurz: "Programm-Sicht auf CMB, KEP, Blue: Arbeitspakete und Nachweise des 1&1-Programms.",
    sehen: ["Programmstruktur und Arbeitspakete (Badge zeigt die offenen), mit Verknüpfung zu Belegen."],
    tun: ["Als Nachschlagewerk für den Programm-Kontext nutzen; Details stehen in OpenProject."],
    hintergrund: "Liest den täglich synchronisierten OpenProject-Stand.",
    daten: "OpenProject-Index (108 Arbeitspakete, täglich 05:30 Uhr).",
    rollen: "Beratersicht.",
    audience: BERATER,
    tags: ["transformation", "programm", "cmb", "kep", "arbeitspaket"],
  },
  {
    id: "roadmap",
    route: "/app/roadmap",
    titel: "Roadmap (90 Tage)",
    kurz: "Der 90-Tage-Plan mit ehrlichem Status je Schritt.",
    sehen: ["Die Plan-Schritte mit Status: im Plan / noch nicht begonnen / in Verzug."],
    tun: ["Status lesen; bei Verzug dem Link in das betroffene Modul folgen."],
    hintergrund: "Status-Anzeige ohne Automatik-Magie.",
    daten: "Programm-Daten der Anwendung.",
    rollen: "Alle (in Kundensicht enthalten).",
    audience: ALLE,
    tags: ["roadmap", "plan", "90 tage", "status"],
  },
  {
    id: "roi",
    route: "/app/roi",
    titel: "ROI / Monte-Carlo",
    kurz: "Business-Case-Szenarien (vorsichtig/mittel/optimistisch) transparent durchrechnen.",
    sehen: ["Szenario-Regler, Ergebnisbänder der Simulation und eine Business-Case-Skizze."],
    tun: ["Annahmen einstellen, Szenarien vergleichen, Ergebnis exportieren."],
    hintergrund: "Rechnet bewusst nachvollziehbar im Browser — keine versteckte Serverlogik.",
    daten: "Ihre Eingaben plus hinterlegte Programmgrößen.",
    rollen: "Beratersicht.",
    audience: BERATER,
    tags: ["roi", "monte-carlo", "szenario", "business case"],
  },
  {
    id: "benchmarks",
    route: "/app/benchmarks",
    titel: "Benchmarks",
    kurz: "Marktvergleich und Einordnung der eigenen Werte.",
    sehen: ["Vergleichswerte und Ranking-Darstellung."],
    tun: ["Zur Einordnung nutzen — Entscheidungen brauchen zusätzlich die eigenen Belege."],
    hintergrund: "Statisch gepflegte Vergleichsdaten.",
    daten: "Hinterlegte Benchmark-Sätze.",
    rollen: "Beratersicht.",
    audience: BERATER,
    tags: ["benchmark", "vergleich", "markt"],
  },
  {
    id: "potential",
    route: "/app/potential",
    titel: "KI-Potenzialanalyse",
    kurz: "Reifegrad, Schmerzpunkte und Quick Wins strukturiert erfassen.",
    sehen: ["Fragen zu Branche, Herausforderungen und Reife; daraus abgeleitete Quick-Win-Hinweise."],
    tun: ["Analyse ausfüllen und Ergebnisse in Use Cases oder Maßnahmen überführen."],
    hintergrund: "Bewusst clientseitig und transparent.",
    daten: "Ihre Eingaben.",
    rollen: "Beratersicht.",
    audience: BERATER,
    tags: ["potenzial", "reifegrad", "quick win"],
  },
  {
    id: "ai-ready-check",
    route: "/app/ai-ready-check",
    titel: "AI-Ready-Check",
    kurz: "Reifegrad-Assessment in 6 Dimensionen — mit Ampeln, Stufe und passenden Interventionen.",
    sehen: [
      "6 Dimensionen (Daten, Governance, Use Case, Skills, Infrastruktur, Change) mit je 3–4 Fragen (Skala 0–4).",
      "Ergebnis: Punktwert und Ampel je Dimension, Gesamt-Reifegrad (Stufe 1–5) und konkrete Empfehlungen inkl. passender Intervention im Wortlaut.",
      "Vergleich zweier Assessments (z. B. Ressort A vs. B oder vorher/nachher).",
    ],
    tun: [
      "Assessment anlegen, Fragen beantworten, Ergebnis speichern.",
      "Empfehlungen je schwacher Dimension lesen und als Maßnahme/Intervention übernehmen.",
      "Für Fachbereiche: Der zugehörige Fragebogen kann als Formular zugewiesen werden.",
    ],
    hintergrund:
      "Punktwerte und Ampeln werden deterministisch berechnet; Empfehlungen ziehen passende Interventionen im Wortlaut aus dem Katalog. " +
      "Eine optionale KI-Zusammenfassung verdichtet nur die berechneten Ergebnisse.",
    daten: "Eigene Datenbanktabelle (je Mandant und Benutzer:in).",
    rollen: "Durchführung in der Beratersicht; Zulieferung des Fachbereichs über das zugewiesene Formular.",
    bilder: [
      {
        src: "/help/ai-ready-check.png",
        alt: "AI-Ready-Check mit Dimensionen, Ampeln und Empfehlungen (synthetische Sandbox)",
        beschriftung: "AI-Ready-Check: sechs Dimensionen, ehrliche Ampeln, Empfehlungen mit Interventions-Bezug.",
      },
    ],
    audience: BERATER,
    tags: ["ai-ready", "reifegrad", "assessment", "dimension", "empfehlung"],
  },
  {
    id: "use-cases",
    route: "/app/use-cases",
    titel: "KI Use Cases",
    kurz: "Kandidaten für KI-Einsatz mit Impact, Readiness und Risiko.",
    sehen: ["Die Use-Case-Liste (aktuell 5) mit Bewertungsdimensionen."],
    tun: ["Use Cases bewerten, priorisieren und mit Kennzahlen/Maßnahmen verknüpfen."],
    hintergrund: "Bewertungslogik in der Anwendung, keine externen Aufrufe.",
    daten: "Anwendungsdatenbank (je Mandant).",
    rollen: "Beratersicht.",
    audience: BERATER,
    tags: ["use case", "impact", "readiness", "risiko"],
  },
  {
    id: "compliance",
    route: "/app/compliance",
    titel: "Compliance & FRIA",
    kurz: "EU-AI-Act-Prüfungen, Audit-Spuren und Dossiers.",
    sehen: ["FRIA-Dossiers und Prüfstände mit Belegbezug."],
    tun: ["Dossiers pflegen und für Audits exportieren."],
    hintergrund: "Baut auf derselben Evidence-Kette auf wie Agent Runs.",
    daten: "Anwendungsdatenbank + Belege.",
    rollen: "Beratersicht/Admin.",
    audience: BERATER,
    tags: ["compliance", "eu ai act", "fria", "audit"],
  },
  {
    id: "achievements",
    route: "/app/achievements",
    titel: "Achievements",
    kurz: "Belegter Fortschritt: Was ist nachweislich erreicht?",
    sehen: ["Erreichte Meilensteine, jeweils mit Beleg."],
    tun: ["Für Statusberichte und Motivation nutzen."],
    hintergrund: "Speist sich aus echten Nachweisen, nicht aus Selbsteinschätzung.",
    daten: "Evidence-/Run-Speicher.",
    rollen: "Beratersicht.",
    audience: BERATER,
    tags: ["achievement", "meilenstein", "fortschritt"],
  },
  {
    id: "company",
    route: "/app/company",
    titel: "Unternehmen (Mandantenprofil)",
    kurz: "Stammdaten des Mandanten und Beraterkontext.",
    sehen: ["Profil, Branche, Kontext-Angaben des Mandanten."],
    tun: ["Profil aktuell halten — es speist Onboarding und Einrichtung."],
    hintergrund: "Stammdaten-Modul ohne Automatik.",
    daten: "Anwendungsdatenbank (je Mandant).",
    rollen: "Beratersicht.",
    audience: BERATER,
    tags: ["unternehmen", "profil", "mandant", "stammdaten"],
  },
  {
    id: "onboarding",
    route: "/app/onboarding",
    titel: "Geführte Einführung",
    kurz: "Der geführte Einstieg: Branche, Ziele, Datenlage Schritt für Schritt.",
    sehen: ["Einen geführten Dialog, der die wichtigsten Kontext-Angaben abfragt."],
    tun: ["Einmal durchlaufen (bzw. mit der Beratung); danach übernehmen die Fachmodule."],
    hintergrund: "Füllt Profil und Ausgangslage — die tiefergehende \"Einrichtung\" lebt im Steuerungssystem.",
    daten: "Anwendungsdatenbank (je Mandant).",
    rollen: "Beratersicht (im Demo-Modus auch als Produkt-Tour sichtbar).",
    audience: BERATER,
    tags: ["onboarding", "einführung", "start"],
  },
  {
    id: "command-center",
    route: "/app/command-center",
    titel: "Command Center / Cockpit (klassisch)",
    kurz: "Die klassische Kennzahlen-Übersicht: Was ist los, warum, was tun.",
    sehen: ["Kennzahl-Karten mit Status und Kurzbegründung; die frühere Startansicht liegt unter \"Klassisches Cockpit\"."],
    tun: ["Als kompakte Alternative zum Steuerkreis-Cockpit nutzen."],
    hintergrund: "Liest dieselben geprüften Werte wie alle Cockpits.",
    daten: "Geprüfter Werte-Weg (halbstündlich).",
    rollen: "Beratersicht.",
    audience: BERATER,
    tags: ["command center", "cockpit", "klassisch"],
  },
  {
    id: "exporte",
    route: "/app/briefing-agent",
    titel: "Exporte & Berichte",
    kurz: "Wo Sie Daten und Berichte aus der Anwendung herausbekommen — und was bewusst nicht angeboten wird.",
    sehen: [
      "Board-Pack im Management-Briefing: Markdown, CSV, Excel, JSON.",
      "Formular-Antworten: CSV (Semikolon-getrennt, Excel-tauglich).",
      "Briefing-Downloads mit zeitlich begrenzten Links (7 Tage).",
    ],
    tun: [
      "Im Briefing-Modul \"Export\" wählen und das Format bestimmen.",
      "In Formularen die Antworten-Tabelle als CSV herunterladen.",
    ],
    hintergrund: "Exporte werden als echte Dateien im internen Objektspeicher erzeugt — nichts verlässt die Plattform automatisch.",
    daten: "Jeweils die geprüften Daten des erzeugenden Moduls zum Zeitpunkt des Exports.",
    rollen: "Export kann jede:r Berechtigte im jeweiligen Modul auslösen.",
    grenzen: "PDF/PowerPoint werden bewusst nicht erzeugt; verlässlich sind Markdown/CSV/Excel/JSON.",
    audience: ALLE,
    tags: ["export", "csv", "excel", "markdown", "download", "bericht"],
  },
];

/* ================================================================== */
/* 3) ZUSAMMENWIRKEN DER SYSTEME                                       */
/* ================================================================== */

export const SYSTEME: SystemBlock[] = [
  {
    id: "openproject",
    name: "OpenProject (Projektsystem)",
    absatz:
      "OpenProject bleibt das führende System für Arbeitspakete. Die Plattform liest den Stand einmal täglich (05:30 Uhr) " +
      "und zeigt ihn im Kanban-Board, in den Ampeln und an den Treiberbaum-Knoten. Das Cockpit zeigt bewusst alle 108 Arbeitspakete " +
      "(volle Programmsicht); der Chatbot arbeitet mit den 16 ausdrücklich freigegebenen (kuratierter Governance-Ausschnitt) — beides ist richtig.",
    merksatz:
      "Die App schreibt NICHTS nach OpenProject zurück: Statuswechsel erzeugen nur einen dokumentierten Freigabe-Eintrag; die echte Übernahme bleibt gesperrt, bis sie ausdrücklich freigeschaltet wird.",
  },
  {
    id: "nextcloud",
    name: "Nextcloud (Dokumente)",
    absatz:
      "Dokumente aus dem freigegebenen Projektbereich (u. a. PowerPoint, Word, PDF, Excel, Fotos) werden automatisch gelesen, " +
      "in Text umgewandelt (auch Fotos von Whiteboards) und in die Wissensbasis des Lakehouse übernommen. Danach finden Advisor, " +
      "Prozessschritt-Suche und die Chatbots diese Inhalte mit Quellenangabe. Neue Inhalte erscheinen typischerweise innerhalb weniger Stunden.",
    merksatz:
      "Nur der freigegebene Bereich wird gelesen. Wird eine Datei dort herausbewegt, verschwindet sie aus künftigen Antworten — bereits erzeugte Berichte bleiben (das sind Kopien).",
  },
  {
    id: "meetily",
    name: "Meetily (Besprechungen)",
    absatz:
      "Aus Besprechungen gelangt nur die freigegebene Ableitung (Protokoll/Transkript-Auszug) über den freigegebenen Pfad in die Wissensbasis — " +
      "das Meeting-Cockpit zeigt genau diese indexierten Protokolle. Daraus lassen sich Zusammenfassung, Aufgaben und Entscheidungen ableiten und " +
      "kontrolliert ins Entscheidungs-Log übernehmen.",
    merksatz: "Die Rohaufnahme bleibt geschützt und wird nie frei durchsuchbar. Personenbezogene Passagen werden vor Freigabe entfernt.",
  },
  {
    id: "lakehouse",
    name: "Data Lakehouse (geprüfte Datenbasis)",
    absatz:
      "Alle Daten durchlaufen drei Stufen: Rohdaten (Bronze) → bereinigt (Silver) → geprüfte, auswertbare Wahrheit (Gold). " +
      "Vor jeder Veröffentlichung laufen automatische Qualitätsprüfungen; schlägt eine fehl, bricht die Kette ab und nichts Ungeprüftes wird sichtbar. " +
      "Für Inhalte gilt das Qualitäts-Gate: PASS = veröffentlicht und sichtbar · REVIEW = wartet auf menschlichen Prüfentscheid (bei sensiblen Inhalten automatisch) · BLOCK = erreicht keine Antworten. " +
      "Die Cockpits lesen ausschließlich aus dieser geprüften Ebene.",
    merksatz:
      "Gold ist die geprüfte Wahrheit. Fehler werden IMMER an der Quelle korrigiert — nie im Suchindex, sonst kommt der alte Stand beim nächsten Lauf zurück.",
  },
  {
    id: "chatbots",
    name: "Chatbots (was frage ich wo?)",
    absatz:
      "1&1-KIM-Chat (1und1-chat.ki-guru.com): Wissensrecherche im 1&1-Bestand, Faktenfragen mit Beleg, Interventionsempfehlung — lokales Modell, " +
      "liest live dieselben geprüften Golddaten. · In dieser App: \"Frag die Daten\" für schnelle Programm-Faktenfragen, der Advisor für Einordnung und Sparring. · " +
      "Impulse-Chat (chat.impulse.kim): Berater-Methodik mit dem generischen Interventionskatalog — bewusst ohne 1&1-Interna.",
    merksatz: "Alle Wege sind mandantengetrennt: Der 1&1-Chat sieht nur 1&1-Inhalte, der Impulse-Chat nur die Berater-Methodik.",
  },
  {
    id: "email",
    name: "E-Mail & Benachrichtigungen",
    absatz:
      "Die Glocke in der App ist der zuverlässige Kanal: Ein Prüf-Lauf alle 15 Minuten erzeugt Hinweise nach Ihren Einstellungen, geschützt gegen Doppelmeldungen. " +
      "Der E-Mail-Versand ist technisch fertig eingerichtet (inklusive Schutz vor Doppelversand) und startet, sobald das bestätigte Postfach-Passwort hinterlegt ist.",
    merksatz: "Bis zur E-Mail-Freischaltung gilt: Alles Wichtige steht zuverlässig in der Glocke und unter Benachrichtigungen.",
  },
];

/* ================================================================== */
/* 4) AGENTEN-LANDKARTE                                                */
/* ================================================================== */

export const AGENTEN: AgentZeile[] = [
  {
    agent: "Proaktive KI (\"Nächste Schritte\")",
    wann: "Bei jedem Aufruf der Startseite (plus 15-Minuten-Prüf-Lauf)",
    liest: "Kennzahlen, Ressort-Verzug, Arbeitspakete, Prozessstände",
    erzeugt: "Priorisierte Vorschläge mit Begründung und Direktlink",
    sichtbar: "Startübersicht (\"Nächste Schritte\")",
  },
  {
    agent: "Erinnerungs-Prüfung (Reminder-Scan)",
    wann: "Alle 15 Minuten",
    liest: "Ihre Auslöser-Einstellungen + Programmdaten",
    erzeugt: "In-App-Hinweise (gegen Doppelmeldung geschützt)",
    sichtbar: "Glocke + Benachrichtigungen",
  },
  {
    agent: "Briefing-Agent",
    wann: "Auf Abruf (Lauf ≈ 90 Sekunden)",
    liest: "Kennzahlen, Treiberbaum, Datenvertrauen, OE-Board/Arbeitspakete",
    erzeugt: "Management-Briefing mit Belegen + Exporte",
    sichtbar: "Management-Briefing, Steuerkreis-Cockpit, Agent Runs",
  },
  {
    agent: "KPI-Werte-Übernahme",
    wann: "Halbstündlich (:05 und :35)",
    liest: "Angelieferte Kennzahlen-Dateien (fester Datenvertrag)",
    erzeugt: "Geprüfte Messwerte in der Werte-Tabelle",
    sichtbar: "Alle Cockpits, KPI-Katalog, Treiberbäume",
  },
  {
    agent: "Qualitäts-Lauf (dbt-Prüfungen)",
    wann: "Halbstündlich (:12 und :42)",
    liest: "Rohdaten (Bronze)",
    erzeugt: "Geprüfte Gold-Sichten; bei rotem Test bricht die Kette ab",
    sichtbar: "Cockpits + Gold-Dashboards",
  },
  {
    agent: "OpenProject-Abgleich",
    wann: "Täglich 05:30 Uhr",
    liest: "OpenProject (nur lesend)",
    erzeugt: "Arbeitspaket-Index (108 Pakete)",
    sichtbar: "Kanban, Ampeln, Transformation, Treiberbäume",
  },
  {
    agent: "Dokumenten-Lauf (Nextcloud)",
    wann: "Regelmäßig, lastgeschützt (Inhalte innerhalb weniger Stunden)",
    liest: "Freigegebene Nextcloud-Dokumente",
    erzeugt: "Durchsuchbare Wissensbasis-Einträge (mit Texterkennung)",
    sichtbar: "Advisor, Prozessschritt-Wissen, Chatbots, Meeting-Cockpit",
  },
  {
    agent: "Ablage-Eingang (Dropzone)",
    wann: "Alle 10 Minuten",
    liest: "Ad-hoc abgelegte Dateien im Eingangsordner",
    erzeugt: "Wissensbasis-Einträge (mit Herkunftsnachweis; Unlesbares geht in Quarantäne)",
    sichtbar: "Wissensbasis/Suche",
  },
  {
    agent: "Interventionskatalog-Eingang",
    wann: "Alle 10 Minuten",
    liest: "Excel-/Word-Anlieferungen im Katalog-Eingang",
    erzeugt: "Neue Katalog-Version (geprüft, versioniert)",
    sichtbar: "Interventionskatalog + Chatbots",
    beraterOnly: true,
  },
  {
    agent: "Wissens-Aktualisierung (Stufenlauf)",
    wann: "Stündlich",
    liest: "OpenProject, Nextcloud, Protokolle, Kennzahlen, Produkte",
    erzeugt: "Aktualisierte Wissens-Schnappschüsse für die Chatbots",
    sichtbar: "1&1-KIM-Chat, Suche",
  },
  {
    agent: "Bedeutungs-Index (Vektor-Sync)",
    wann: "Stündlich (:20)",
    liest: "Neue Wissensbasis-Einträge",
    erzeugt: "Bedeutungs-Suchindex (findet auch sinngemäße Treffer)",
    sichtbar: "Hybrid-Suche überall",
  },
  {
    agent: "Selbstüberwachung",
    wann: "Alle 20 Minuten",
    liest: "Dienste, Zeitpläne, Rückstände",
    erzeugt: "Gesundheits-Ampel; kleine Selbstheilung nur in engen Grenzen",
    sichtbar: "Dashboard \"Systemgesundheit\" (Betrieb)",
    beraterOnly: true,
  },
  {
    agent: "Backups",
    wann: "Täglich (02:30 verschlüsselt, 04:15 Spiegel)",
    liest: "Sicherungsordner der Plattform",
    erzeugt: "Off-Node-Sicherungen",
    sichtbar: "Betrieb (nicht in der App)",
    beraterOnly: true,
  },
];

/* ================================================================== */
/* 5) WER DARF WAS (Rollen-Matrix in Kundenworten)                     */
/* ================================================================== */

export const ROLLEN_MATRIX: MatrixZeile[] = [
  { taetigkeit: "Cockpits, Ampeln und Kennzahlen ansehen", fachbereich: "Ja", management: "Ja", berater: "Ja" },
  { taetigkeit: "\"Frag die Daten\" nutzen / Briefing lesen", fachbereich: "Ja", management: "Ja", berater: "Ja" },
  { taetigkeit: "Formulare ausfüllen", fachbereich: "Ja (zugewiesene)", management: "Ja", berater: "Ja" },
  { taetigkeit: "Formulare erstellen, zuweisen und auswerten", fachbereich: "—", management: "—", berater: "Ja" },
  { taetigkeit: "Eigene Kanban-Karten anlegen und verschieben", fachbereich: "Ja", management: "Ja", berater: "Ja" },
  { taetigkeit: "Statuswechsel an OpenProject-Karten freigeben", fachbereich: "—", management: "Ja (mit Begründung)", berater: "Ja (mit Begründung)" },
  { taetigkeit: "Entscheidungen im Steuerungssystem freigeben/ablehnen/vertagen", fachbereich: "—", management: "Ja (mit Begründung + Nachweis)", berater: "Ja (mit Begründung + Nachweis)" },
  { taetigkeit: "Zu Prozessschritten zuliefern (Notizen, Links, Unterlagen)", fachbereich: "Ja", management: "Ja", berater: "Ja" },
  { taetigkeit: "Prozessschritte starten und mit Nachweis abschließen", fachbereich: "—", management: "—", berater: "Ja (Berater:in/Admin)" },
  { taetigkeit: "KPI-Definitionen, Fachpakete, Einrichtung pflegen", fachbereich: "zuliefern", management: "mitentscheiden", berater: "Ja" },
  { taetigkeit: "Interventionskatalog nutzen und zuordnen", fachbereich: "—", management: "—", berater: "Ja" },
  { taetigkeit: "Eigene Benachrichtigungen einstellen", fachbereich: "Ja", management: "Ja", berater: "Ja" },
  { taetigkeit: "Sicht wechseln (Berater-/Kundensicht als Vorschau)", fachbereich: "—", management: "—", berater: "Ja" },
  { taetigkeit: "Mandanten wechseln / Mandanten anlegen", fachbereich: "—", management: "—", berater: "Nur Plattform-Admin" },
];

export const ROLLEN_HINWEISE: string[] = [
  "Ihre Rechte kommen aus der Anmeldung (Keycloak-Rollen) und dem Mandanten — nicht aus der Oberfläche. Die Kundensicht ist eine Verdichtung, kein anderes Rechtesystem.",
  "Jede Zeile in der Datenbank trägt den Mandanten; die Trennung wird doppelt erzwungen (Datenbank-Regel + ausdrücklicher Filter in jeder Abfrage). Ein anderer Mandant kann Ihre Daten auch bei einem Programmfehler nicht sehen.",
  "Sehen Sie weniger Menüpunkte als Kolleg:innen? Dann sind Sie in der Kundensicht oder haben eine andere Rolle — das ist gewollt. Bei falscher Zuordnung: ab- und wieder anmelden, dann die Rollen-Zuordnung vom Admin prüfen lassen.",
];

/* ================================================================== */
/* 6) FAQ                                                              */
/* ================================================================== */

export const FAQ: FaqEintrag[] = [
  {
    frage: "Warum sehe ich keine oder nur wenige Kennzahlen?",
    antwort:
      "Meist ist das Vertrauens-Gate der Grund: Die Anwendung zeigt bewusst keine Werte, wenn Quellen fehlen, veraltet oder zu dünn sind. " +
      "Unter Datenquellen & Quality steht je Quelle, was fehlt. Kennzahlen ohne Messwert erscheinen ehrlich als Platzhalter.",
    route: "/app/sources",
    linkLabel: "Datenvertrauen prüfen",
  },
  {
    frage: "Warum zeigt das Cockpit 108 Arbeitspakete, der Chatbot nur 16?",
    antwort:
      "Kein Widerspruch: 108 = die volle Programmsicht über alle Projektbäume (Steuerung/Reporting). 16 = der eng gefasste, ausdrücklich " +
      "freigegebene Ausschnitt für Chatbot und Datenprodukte. Soll mehr freigegeben werden, ist das eine Governance-Entscheidung, keine Technikfrage.",
  },
  {
    frage: "Warum bekomme ich keine E-Mail-Benachrichtigung?",
    antwort:
      "Der E-Mail-Versand ist eingerichtet, wartet aber noch auf das bestätigte Postfach-Passwort. Bis zur Freischaltung kommen alle Hinweise " +
      "zuverlässig über die Glocke in der App (Prüf-Lauf alle 15 Minuten).",
    route: "/app/benachrichtigungen",
    linkLabel: "Benachrichtigungen öffnen",
  },
  {
    frage: "Ich habe einen Datenfehler gefunden — wo melde ich ihn?",
    antwort:
      "Korrigiert wird IMMER an der Quelle (z. B. im Quellsystem oder der Datei), nie im Suchindex — sonst kehrt der alte Stand beim nächsten " +
      "geprüften Lauf zurück. Melden Sie das Fehlerbild mit Zeitpunkt, Mandant, Ansicht, Aktion und ggf. Run-ID an das Projektteam; " +
      "die Korrektur läuft dann durch die geprüfte Kette (sichtbar meist nach dem nächsten Lauf).",
  },
  {
    frage: "Was bedeutet PASS / REVIEW / BLOCK?",
    antwort:
      "Das Qualitäts-Gate des Lakehouse: PASS = geprüft und veröffentlicht. REVIEW = wartet auf eine menschliche Prüfentscheidung " +
      "(bei sensiblen Inhalten automatisch). BLOCK = erreicht keine Antworten und Cockpits. So kommt nichts Ungeprüftes in Ihre Entscheidungen.",
  },
  {
    frage: "Warum kann eine Entscheidung nicht abgeschlossen werden?",
    antwort:
      "Meist fehlen Pflichtangaben, ein Nachweis oder eine menschliche Prüfung. Öffnen Sie den Fall, lesen Sie die offenen Übergaben und " +
      "schließen Sie sie einzeln — eine Automatisierung darf diese Freigabe bewusst nicht ersetzen.",
    route: "/app/steuerungssystem",
    linkLabel: "Offene Übergaben ansehen",
  },
  {
    frage: "Warum entsteht keine Meeting-Zusammenfassung?",
    antwort:
      "Prüfen Sie, ob das Protokoll bereits im Meeting-Cockpit erscheint — eine Aufnahme allein ist noch kein indexiertes, freigegebenes Protokoll. " +
      "Neue Ableitungen erscheinen nach dem nächsten Ingest-Lauf automatisch.",
    route: "/app/meetings",
    linkLabel: "Meeting-Cockpit öffnen",
  },
  {
    frage: "Warum antwortet der Briefing-Agent nicht?",
    antwort:
      "Erst Datenlage prüfen (Vertrauens-Gate), dann unter Agent Runs nachsehen, ob der Lauf wartet, läuft oder fehlgeschlagen ist. " +
      "Bitte keinen Lauf mehrfach hintereinander starten; Run-ID und Zeitpunkt für die Diagnose notieren.",
    route: "/app/agents",
    linkLabel: "Agent Runs prüfen",
  },
  {
    frage: "Warum sieht eine andere Person andere Inhalte?",
    antwort:
      "Mandant, Rollen und gewählte Sicht bestimmen den Umfang. Oben in der Leiste müssen Mandant und Rolle stimmen. " +
      "Bei falscher Zuordnung: ab- und wieder anmelden, danach die Rollen-/Mandanten-Zuordnung durch einen Admin prüfen lassen.",
  },
  {
    frage: "Was bedeutet \"Temporärer Speicher\" oder \"Sandbox\"?",
    antwort:
      "Das kennzeichnet eine Vorschau-/Übungsumgebung ohne dauerhafte Speicherung (z. B. mit synthetischen Daten wie in den Hilfe-Bildern). " +
      "Produktive Arbeit erst beginnen, wenn die Oberfläche keine solche Kennzeichnung zeigt.",
  },
];

/* ================================================================== */
/* 7) FUNKTIONSSTAND (ehrlich)                                         */
/* ================================================================== */

export const FUNKTIONSSTAND: FeatureStand[] = [
  { name: "Geschlossener Steuerungs-Regelkreis", stand: "live", text: "Kontext, KPI, Abweichung, Entscheidung, Maßnahme, Wirkung und Lernen sind in Steuerungsfällen verbunden." },
  { name: "Kennzahlen aus geprüftem Gold", stand: "live", text: "Der produktive Leseweg nutzt die geprüfte Gold-Ebene; fehlende Werte bleiben als Platzhalter gekennzeichnet." },
  { name: "Vertrauens-Gate (Data Trust)", stand: "live", text: "Erreichbarkeit, Aktualität, Volumen, Qualitäts-Score und Belege werden einzeln sichtbar geprüft." },
  { name: "Proaktive KI + Erinnerungen", stand: "live", text: "\"Nächste Schritte\" auf der Startseite plus 15-Minuten-Prüf-Lauf mit Schutz vor Doppelmeldungen." },
  { name: "Kanban mit Freigabe-Weg", stand: "live", text: "Eigene Karten voll beweglich; Statuswechsel an OpenProject-Karten erzeugen einen dokumentierten Freigabe-Eintrag." },
  { name: "Formulare mit Vorlagen und Zuweisung", stand: "live", text: "Vier fachlich hinterlegte Vorlagen, Zuweisung mit proaktivem Hinweis, Auswertung und CSV." },
  { name: "Management-Briefing mit lokaler KI", stand: "live", text: "Hintergrund-Worker, nur Faktenblock, Belege, Export als MD/CSV/Excel/JSON (Links 7 Tage)." },
  { name: "E-Mail-Versand", stand: "teilweise", text: "Fertig eingerichtet inkl. Schutz vor Doppelversand; wartet auf das bestätigte Postfach-Passwort. Bis dahin: Glocke in der App." },
  { name: "Quellen-Sync per Knopfdruck", stand: "teilweise", text: "Der Knopf dokumentiert den Stand; der eigentliche Datenabzug läuft über die geprüften Hintergrund-Läufe." },
  { name: "Automatisches Schreiben in externe Systeme", stand: "geplant", text: "Bewusst gesperrt: OpenProject und Gold bleiben schreibgeschützt, bis Freigabe, Audit und Rücknahmeweg vollständig abgesichert sind." },
];

/* ================================================================== */
/* 8) DOKU-PORTALE & WEITERFÜHREND                                     */
/* ================================================================== */

export const PORTALE: PortalLink[] = [
  { name: "1&1 Projekt-Doku (docs-1und1aios)", url: "https://docs-1und1aios.ki-guru.com", zweck: "Projekt- und Plattform-Dokumentation für das 1&1-Team (Zugangsdaten vom Projektteam)", zielgruppe: "kunde" },
  { name: "1&1 Betriebs-Doku (1und1docs)", url: "https://1und1docs.ki-guru.com", zweck: "Betriebs- und Lakehouse-Kapitel mit Anmeldung (SSO)", zielgruppe: "kunde" },
  { name: "1&1 KIM-Chat", url: "https://1und1-chat.ki-guru.com", zweck: "Wissensrecherche und Interventionsempfehlung im 1&1-Bestand (lokales Modell)", zielgruppe: "kunde" },
  { name: "Nextcloud: Plattformdokumentation", url: "https://cloud.impulse.kim", zweck: "Anleitungen als Dateien, u. a. \"KIM Data Lakehouse\" (Nutzer-Praxis & Administration)", zielgruppe: "kunde" },
  { name: "Impulse/Berater-Doku (docs.ben-e-fit.ai)", url: "https://docs.ben-e-fit.ai", zweck: "Technische Vollfassung der Plattform-Doku (intern)", zielgruppe: "berater" },
  { name: "Impulse-Doku-Alias (docs.impulse.kim)", url: "https://docs.impulse.kim", zweck: "Gleiche Vollfassung unter Impulse-Domain", zielgruppe: "berater" },
  { name: "Impulse-Chat", url: "https://chat.impulse.kim", zweck: "Berater-Methodik und generischer Interventionskatalog (mandantenübergreifend, ohne 1&1-Interna)", zielgruppe: "berater" },
  { name: "OpenSearch-Dashboards", url: "https://opensearch.ben-e-fit.ai", zweck: "Cockpit-, OE-Board- und Systemgesundheits-Dashboards (öffnen in neuem Tab)", zielgruppe: "berater" },
];

/* ================================================================== */
/* 9) MELDEWEG                                                          */
/* ================================================================== */

export const MELDEWEG = {
  einleitung:
    "Wenn etwas fehlt, falsch aussieht oder sich nicht bedienen lässt: Bitte melden statt umgehen. " +
    "Mit einem guten Fehlerbild ist die Ursache meist schnell gefunden.",
  checkliste: [
    "Zeitpunkt (Datum + Uhrzeit)",
    "Sichtbarer Mandant und aktive Sicht (oben in der Leiste)",
    "Ansicht/Modul und die konkrete Aktion",
    "Fehlermeldung im Wortlaut (Screenshot ohne vertrauliche Inhalte)",
    "Falls vorhanden: Run-ID aus Agent Runs",
  ],
  grundsaetze: [
    "Datenfehler werden an der Quelle korrigiert — nie im Suchindex (der alte Stand käme beim nächsten Lauf zurück).",
    "Rote Gates oder Sperren nicht umgehen, sondern Ursache beheben oder melden.",
    "\"Nicht belegbar\" kann die richtige Antwort sein — z. B. wenn ein Dokument aus dem freigegebenen Bereich entfernt wurde.",
  ],
  kontakt:
    "Meldung an das Projektteam (Impulse-Beratung) — z. B. direkt im nächsten Termin oder über die Berater:in Ihres Ressorts.",
};
